import React, { useState } from 'react';
import Snackbar from '../../Snackbar/Snackbar';
import useDevices from '../../../hooks/useDevices/useDevices';
import useVideoContext from '../../../hooks/useVideoContext/useVideoContext';
import i18next from 'i18next';

export function getSnackbarContent(hasAudio: boolean, hasVideo: boolean, error?: Error) {
  let headline = '';
  let message = '';

  switch (true) {
    // These custom errors are thrown by the useLocalTracks hook. They are thrown when the user explicitly denies
    // permission to only their camera, or only their microphone.
    case error?.message === 'CameraPermissionsDenied':
      headline = i18next.t('mediaAccess.error');
      message =  i18next.t('videoPermission.denied');
      break;
    case error?.message === 'MicrophonePermissionsDenied':
      headline = i18next.t('mediaAccess.error');
      message =  i18next.t('audioPermission.error');
      break;

    // This error is emitted when the user or the user's system has denied permission to use the media devices
    case error?.name === 'NotAllowedError':
      headline = i18next.t('mediaAccess.error');

      if (error!.message === 'Permission denied by system') {
        // Chrome only
        message = i18next.t('osPermission.blocked')
      } else {
        message = i18next.t('mediaPermission.denied');
      }

      break;

    // This error is emitted when input devices are not connected or disabled in the OS settings
    case error?.name === 'NotFoundError':
      headline = i18next.t('mediaDevice.notFound');
      message =  i18next.t('mediaDevice.accessError');
      break;

    // Other getUserMedia errors are less likely to happen in this app. Here we will display
    // the system's error message directly to the user.
    case Boolean(error):
      headline = 'Error Acquiring Media:';
      message = `${error!.name} ${error!.message}`;
      break;

    case !hasAudio && !hasVideo:
      headline = i18next.t('mediaDevice.notDetected');
      message = i18next.t('participant.noAudioVideo');
      break;

    case !hasVideo:
      headline = i18next.t('camera.notDetected');
      message = i18next.t('participant.noVideo');
      break;

    case !hasAudio:
      headline = i18next.t('microphone.notDetected');
      message = i18next.t('participant.noAudio');
  }

  return {
    headline,
    message,
  };
}

export default function MediaErrorSnackbar({ error }: { error?: Error }) {
  const { hasAudioInputDevices, hasVideoInputDevices } = useDevices();

  const { isAcquiringLocalTracks } = useVideoContext();

  const [isSnackbarDismissed, setIsSnackbarDismissed] = useState(false);

  const isSnackbarOpen =
    !isSnackbarDismissed &&
    !isAcquiringLocalTracks &&
    (Boolean(error) || !hasAudioInputDevices || !hasVideoInputDevices);

  const { headline, message } = getSnackbarContent(hasAudioInputDevices, hasVideoInputDevices, error);

  return (
    <Snackbar
      open={isSnackbarOpen}
      handleClose={() => setIsSnackbarDismissed(true)}
      headline={headline}
      message={message}
      variant="warning"
    />
  );
}
